import { Icon, Intent, Position } from "@blueprintjs/core";
import axios from "axios";
import { PieChart, StackedBarChart } from "components/charts";
import { Numeric } from "components/formatting";
import { FormLocationSelector } from "components/form-fields";
import { GridLayout } from "components/grid";
import { Tabs } from "components/navigation";
import { NotificationToaster } from "components/notifications";
import { Tooltip } from "components/tooltip";
import { HubTile } from "components/hubs/hub-tile";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { EmployeeAssessmentsService, EmployeeService } from "services";
import { riskFillColours } from "../riskColours";

const overallRiskSection = { sectionKey: 'CATEGORYANDRISKRATING', dimensionKey: 'overall', label: 'Overall Risk' };

const dimensionSections = [
    { sectionKey: 'HAZARDPERCEPTIONRISK', dimensionKey: 'hazardPerception', label: 'Hazard Perception Risk' },
    { sectionKey: 'ATTITUDERISK', dimensionKey: 'attitude', label: 'Attitude Risk' },
    { sectionKey: 'KNOWLEDGERISK', dimensionKey: 'knowledge', label: 'Knowledge Risk' },
    { sectionKey: 'OBSERVATIONRISK', dimensionKey: 'observation', label: 'Observation Risk' }
];

const overallRiskBands = ['High', 'Medium', 'Low'];

const allCategoriesKey = 'all';

const completionSection = { sectionKey: 'COMPLETION', label: 'Incomplete Assessments', dummyNumberOfStats: 4 };

export function EmployeeAssessmentsOverview() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isInitallyLoading, setIsInitiallyLoading] = useState(true);
    const reduxLocations = useSelector(
        (state) => state.employeeAssessments.locations
    );

    const dispatch = useDispatch();

    const buildDummyStats = () => {
        const completionDummies = Array.from(
            { length: completionSection.dummyNumberOfStats },
            (_, i) => ({
                id: i,
                filterSectionKey: completionSection.sectionKey,
                filterName: `${completionSection.label} Stat ${i + 1}`,
                filterKey: `${completionSection.sectionKey}_STAT_${i + 1}`,
                displayOrder: i + 1,
                displayColour: '',
                risk: null,
                employeeCategory: null
            })
        );

        const allSections = [overallRiskSection, ...dimensionSections];

        const pieDummies = allSections.flatMap(section => {
            return Array.from({ length: 3 }, (_, i) => ({
                id: 1000 + (allSections.indexOf(section) * 10) + i,
                filterSectionKey: section.sectionKey,
                filterName: `${section.label} Stat ${i + 1}`,
                filterKey: `${section.sectionKey}_STAT_${i + 1}`,
                displayOrder: i + 1,
                displayColour: '',
                risk: null,
                employeeCategory: null
            }));
        });

        return [...completionDummies, ...pieDummies];
    };

    const [employeeAssessmentOptions, setEmployeeAssessmentOptions] = useState(
        {
            trainingTypes: [],
            riskScores: [{
                id: 1,
                name: "High"
            }, {
                id: 2,
                name: "Medium"
            }, {
                id: 3,
                name: "Low"
            }],
            statsFilters: buildDummyStats()
        }
    );

    const [stats, setStats] = useState([
        {
            id: 0,
            count: 0
        }
    ]);

    const [employeeCategories, setEmployeeCategories] = useState([]);

    const [useLocationDefaults, setUseLocationDefaults] = useState(null);
    const [useHierarchy, setUseHierarchy] = useState(true);

    // Source of truth for eligible categories is the stats lookup, which only seeds
    // (category x band) rows for categories with a default external vehicle type.
    const eligibleEmployeeCategoryIds = new Set(
        employeeAssessmentOptions.statsFilters
            .filter(stat => stat.employeeCategory)
            .map(stat => stat.employeeCategory)
    );

    const eligibleEmployeeCategories = employeeCategories.filter(
        category => eligibleEmployeeCategoryIds.has(category.id)
    );

    function buildGroup(section) {
        const sectionStats = employeeAssessmentOptions.statsFilters
            .filter(stat => stat.filterSectionKey === section.sectionKey)
            .map(stat => {
                const matchingStat = matchStatCount(stat);
                return {
                    ...stat,
                    employeeCategoryName: eligibleEmployeeCategories.find(c => c.id === stat.employeeCategory)?.name,
                    count: matchingStat?.count || 0
                };
            })
            .sort((a, b) => a.displayOrder - b.displayOrder);

        return { ...section, stats: sectionStats };
    }

    function matchStatCount(stat) {
        return stats.find(s => s.id === stat.id);
    }

    const completionGroup = buildGroup(completionSection);
    const overallRiskGroup = buildGroup(overallRiskSection);
    const dimensionGroups = dimensionSections.map(buildGroup);

    const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(allCategoriesKey);

    const categoryToggleItems = [
        { id: allCategoriesKey, name: 'All' },
        ...eligibleEmployeeCategories
    ];

    const categoryTabSettings = categoryToggleItems.map(category => {
        return {
            title: category.name.toUpperCase(),
            key: `category-tab-${category.id}`,
            selected: selectedCategoryFilter === category.id
        };
    });

    function onCategoryTabClick(tabIndex) {
        const tabbedItem = categoryToggleItems[tabIndex];
        if (tabbedItem) {
            setSelectedCategoryFilter(tabbedItem.id);
        }
    }

    useEffect(() => {
        axios.all([fetchStats(), fetchOptions()]).then(() => {
            setIsInitiallyLoading(false);
            setIsLoading(false);
        });
    }, []);

    const fetchStats = () => {
        setIsLoading(true);

        return EmployeeAssessmentsService.getStats(reduxLocations).then(
            statsResult => {
                setStats(statsResult);

                if (!isInitallyLoading) {
                    setIsLoading(false);
                }
            }, () => {
                NotificationToaster.show(Intent.DANGER, "Error fetching employee assessments overview data. Please try again later.");
                throw new Error("Error fetching employee assessments overview stats");
            });
    };

    const fetchOptions = () => {
        return axios.all([
            EmployeeAssessmentsService.getOptions(),
            EmployeeService.getEmployeeOptions()
        ]).then(
            axios.spread((optionsResult, employeeOptionsResult) => {
                setEmployeeAssessmentOptions(optionsResult);
                setEmployeeCategories(employeeOptionsResult.employeeCategories);
            }), () => {
                NotificationToaster.show(Intent.DANGER, "Error fetching employee assessments overview data. Please try again later.");
                throw new Error("Error fetching employee assessments overview options");
            }
        );
    };

    useEffect(() => {
        if (reduxLocations && reduxLocations.length > 0) {
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }

        fetchStats();
    }, [reduxLocations]);

    function onSelectedLocationsChange(locations) {
        dispatch({
            type: "SET_EMPLOYEE_ASSESSMENTS_LOCATIONS",
            payload: locations,
        });
    }

    const generatePieChartData = (riskRating, group) => {
        if (!eligibleEmployeeCategories?.length) {
            return [];
        }

        const relevantStats = group.stats
            .filter(stat => stat.risk === riskRating && eligibleEmployeeCategoryIds.has(stat.employeeCategory))
            .sort((a, b) => a.displayOrder - b.displayOrder);

        const totalForRiskRating = relevantStats.reduce((sum, stat) => sum + stat.count, 0);

        if (totalForRiskRating === 0) {
            return [];
        }

        return relevantStats.map(stat => {
            const employeeCategory = eligibleEmployeeCategories.find(c => c.id === stat.employeeCategory);

            return {
                id: stat.id,
                value: stat.count,
                label: employeeCategory?.name ?? stat.employeeCategoryName,
                categoryId: stat.employeeCategory
            };
        });
    };

    const totalForBand = (riskRating, group) => {
        return group.stats
            .filter(stat => stat.risk === riskRating && eligibleEmployeeCategoryIds.has(stat.employeeCategory))
            .reduce((sum, stat) => sum + stat.count, 0);
    };

    // Stacked bar items for the overall risk total breakdown; bands with a
    // zero count are dropped so they don't render as empty legend entries.
    const overallRiskItems = overallRiskBands
        .map((bandName) => ({
            id: bandName,
            label: bandName,
            value: totalForBand(bandName, overallRiskGroup),
            color: riskFillColours[bandName]
        }))
        .filter((item) => item.value > 0);

    function navigateToRiskListing({ dimensionKey, bandName, categoryId }) {
        const params = new URLSearchParams({ dimension: dimensionKey });
        if (bandName) {
            params.set('band', bandName);
        }
        if (categoryId !== undefined && categoryId !== null && categoryId !== allCategoriesKey) {
            params.set('category', String(categoryId));
        }
        navigate(`/driver-assessments/risk?${params.toString()}`);
    }

    function onPieChartClick(dimensionKey, bandName) {
        navigateToRiskListing({ dimensionKey, bandName });
    }

    function onOverallRiskSliceClick(bandName, sliceItem) {
        navigateToRiskListing({
            dimensionKey: overallRiskGroup.dimensionKey,
            bandName,
            categoryId: sliceItem?.categoryId
        });
    }

    // "All" toggle reads the lookup totals rows (Employee_Category_ID IS NULL);
    // a specific category reads the matching (category x band) rows.
    function generateDimensionPieData(group) {
        const isAllSelected = selectedCategoryFilter === allCategoriesKey;

        const matchingStats = group.stats.filter(stat => {
            if (!stat.risk) {
                return false;
            }
            if (isAllSelected) {
                return !stat.employeeCategory;
            }
            return stat.employeeCategory === selectedCategoryFilter;
        });

        const slices = ['High', 'Medium', 'Low']
            .map(bandName => {
                const matchingStat = matchingStats.find(s => s.risk === bandName);
                return {
                    label: bandName,
                    value: matchingStat?.count ?? 0,
                    color: riskFillColours[bandName]
                };
            })
            .filter(slice => slice.value > 0);

        const total = slices.reduce((sum, slice) => sum + slice.value, 0);

        return { slices, total };
    }

    function onDimensionPieClick(dimensionKey) {
        navigateToRiskListing({
            dimensionKey,
            categoryId: selectedCategoryFilter
        });
    }

    function onDimensionSliceClick(dimensionKey, sliceItem) {
        navigateToRiskListing({
            dimensionKey,
            bandName: sliceItem?.label,
            categoryId: selectedCategoryFilter
        });
    }

    return (
        <>
            <div className="inline-items spacer-bottom">
                <FormLocationSelector
                    businessArea={"Tacho"}
                    loading={isInitallyLoading}
                    selectedLocations={reduxLocations}
                    setSelectedLocations={onSelectedLocationsChange}
                    useLocationDefaults={useLocationDefaults}
                    useHierarchy={useHierarchy}
                    setUseHierarchy={setUseHierarchy}
                    locationDefaultsToTrue={isLoading ? null : true}
                />
                <Tooltip
                    position={Position.RIGHT}
                    content={
                        <div>
                            <h4>Location Filter</h4>
                            <p>
                                This location filter is applied to all tabs in this hub.
                            </p>
                        </div>
                    }
                >
                    <Icon
                        className="hub-tool-tip"
                        icon="info-sign"
                        iconSize={15}
                        color="#0C2235"
                    />
                </Tooltip>
            </div>

            <h2>{completionGroup.label}</h2>
            <GridLayout
                numberOfColumns={6}
                gap={10}
                className="spacer-bottom"
            >
                {completionGroup.stats.map(stat => {
                    return <HubTile
                        key={stat.id}
                        isLoading={isLoading}
                        title={stat.filterName}
                        count={stat?.count}
                        colour={stat.displayColour}
                        link={`/driver-assessments/upcoming?filter=${stat.filterKey}`}
                    />;
                })}
            </GridLayout>

            <h2>{overallRiskGroup.label}</h2>
            <div className="spacer-bottom-small">
                <StackedBarChart
                    title="Risk Breakdown"
                    items={overallRiskItems}
                    loading={isLoading}
                    onItemClick={(item) => onPieChartClick(overallRiskGroup.dimensionKey, item.label)}
                />
            </div>
            <GridLayout
                numberOfColumns={3}
                gap={10}
                className="spacer-bottom"
            >
                {[...employeeAssessmentOptions.riskScores].map(r => {
                    const total = totalForBand(r.name, overallRiskGroup);
                    return (
                        <button
                            type="button"
                            key={`${overallRiskGroup.sectionKey}-${r.id}`}
                            onClick={() => onPieChartClick(overallRiskGroup.dimensionKey, r.name)}
                            className="clickable-chart-wrapper"
                        >
                            <PieChart
                                title={`${r.name} Risk Employees`}
                                loading={isLoading}
                                items={generatePieChartData(r.name, overallRiskGroup)}
                                noDataMessage={`No employees with ${r.name.toLowerCase()} ${overallRiskGroup.label.toLowerCase()}.`}
                                legend={{ orient: 'horizontal', left: 'center', bottom: 0 }}
                                centerValue={<Numeric>{total}</Numeric>}
                                onSliceClick={(sliceItem) => onOverallRiskSliceClick(r.name, sliceItem)}
                            />
                        </button>
                    );
                })}
            </GridLayout>

            <h2>Risk By Category</h2>
            <div className="spacer-bottom-small">
                <Tabs
                    tabs={categoryTabSettings}
                    onClick={onCategoryTabClick}
                    loading={isInitallyLoading}
                    variant="pill"
                />
            </div>
            <GridLayout
                numberOfColumns={4}
                gap={10}
                className="spacer-bottom"
            >
                {dimensionGroups.map(group => {
                    const { slices, total } = generateDimensionPieData(group);
                    const pieChart = (
                        <PieChart
                            title={group.label}
                            loading={isLoading}
                            items={slices}
                            noDataMessage={`No employees with a ${group.label.toLowerCase()} score.`}
                            legend={{ orient: 'horizontal', left: 'center', bottom: 0 }}
                            centerValue={<Numeric>{total}</Numeric>}
                            onSliceClick={(sliceItem) => onDimensionSliceClick(group.dimensionKey, sliceItem)}
                        />
                    );

                    // Whole-pie click only adds a category filter on top of the dimension,
                    // so it's a no-op when "All" is selected. Render as a plain wrapper to
                    // avoid showing the user a click affordance that does nothing.
                    if (selectedCategoryFilter === allCategoriesKey) {
                        return <div key={group.sectionKey}>{pieChart}</div>;
                    }

                    return (
                        <button
                            type="button"
                            key={group.sectionKey}
                            onClick={() => onDimensionPieClick(group.dimensionKey)}
                            className="clickable-chart-wrapper"
                        >
                            {pieChart}
                        </button>
                    );
                })}
            </GridLayout>
        </>
    );
}
